/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {createPortal} from 'react-dom';
import {makeStyles} from '@material-ui/core';
import classnames from 'classnames';
import RootManager from '../client/RootManager';
import {dlsZIndex} from '../theme/theme';

// defines the props definition for the IonPortal component...
export interface IonPortalProps {
  id: string;
  children: React.ReactNode;
  overrideClassName?: string;
  show?: boolean;
  zIndex?: number;
};

interface StyleProps {
  zIndex?: number;
};

// defines the default styles definition for the portal...
const useStyles = makeStyles((theme) => ({
  portal : {
    position                       : 'relative',
    zIndex                         : ({zIndex}: StyleProps) => zIndex ?? dlsZIndex('defaultPortalRoot'),
    right                          : 12,
    top                            : 125,
    [theme.breakpoints.down('sm')] : {
      right : 0
    }
  }
}));

/**
 * Component that can be used when you want to create a portal
 * wrap a child component(s). If the show is falsy then it will
 * remove the element from the DOM and it simply passes undefined
 * and if it is true this component will create a new portal.
 *
 * ```typescript
 *
 *  import {IonPortal} from '@dls/src/render';
 *
 *  <IonPortal id='map-tool' show>
 *    <LayersTool size={20} />
 *  </IonPortal>
 *
 * ```
 */
export const IonPortal = ({
  id,
  children,
  overrideClassName,
  show = true,
  zIndex = null
}: IonPortalProps) => {
  const classes  = useStyles({zIndex});
  const uniqueId = `${id}_portal`;
  const target   = useRef(null);

  useEffect(() => {
    let container = RootManager.getContainer(uniqueId);
    if (!container) {
      container = RootManager.createContainer({
        id    : uniqueId,
        class : classnames(classes.portal, overrideClassName)
      });
      document.body.appendChild(container);
    }
    const childNodes = container?.childNodes;
    if (childNodes?.length === 1) {
      unMount(childNodes[0]);
    }

    container.appendChild(target.current);

    return () => {
      unMount(target.current);
      const childNodes = container?.childNodes;
      if (childNodes?.length === 0) {
        unMount(container);
      } else if (childNodes?.length === 1) {
        unMount(container.childNodes[0]);
      }
    };
  }, [uniqueId, show]);

  if (!target.current) {
    target.current =  RootManager.createContainer();
  }

  const unMount = useCallback((container?: any) => {
    RootManager.removeContainer(container || RootManager.getContainer(uniqueId));
  }, [uniqueId]);

  return createPortal(children, target.current);
};

export default IonPortal;
